import { type JanusCtx, type Row, ecId, ecLean, itemField, scalar } from '../context.js';
import { encounterField, posCoord } from '../../../shared/array-types.js';
import { discernVariableTags } from '../discern-variable-tags.js';
import { escapeHtml } from '../../../shared/cosmetic-card.js';
import { skipUnported } from '../../unported.js';

/** A successful effect earns one landing choice. It never adds ordinary movement or chooses a destination for its controller. */
export interface AbilityTeleportOffer {
  id: string;
  casterId: string;
  abilityId: string;
  name: string;
  rangeFt: number;
  origin: { x: number; y: number; z: number };
  context: string[];
  triggered: boolean;
  expires: number;
}

/** Attack approach markers yield to the successful hit's controller-chosen landing. */
export function attackGrantsTeleportChoice(ability: Row, read: Pick<JanusCtx['read'], 'value'>): boolean {
  return ['attack_ability', 'opportunity_attack_ability'].includes(String(itemField(ability, 'item_type') ?? ''))
    && Number(scalar(read.value(ability, 'change_teleport_remaining'))) > 0;
}

/** ft_apply_teleport_effect / ft_queue_teleport_offer: successful post-effect only, once per dispatch (including multi-target effects). */
export function applyAbilityTeleport(ctx: JanusCtx, ability: Row, caster: Row): boolean {
  if (!ctx.read.has(ability, 'change_teleport_remaining')) return false;
  const raw = scalar(ctx.read.value(ability, 'change_teleport_remaining'));
  const rangeFt = discernVariableTags(ctx, raw, caster, { reason: 'ability-teleport' });
  if (!(rangeFt > 0)) return false;
  const casterId = ecId(caster), abilityId = String(itemField(ability, 'item_id') ?? '').toUpperCase();
  if (!casterId) return true;
  const key = `${casterId}|${abilityId}`;
  const frame = ctx.teleportFrame ??= new Set();
  if (frame.has(key)) return true;
  frame.add(key);
  if (ctx.dryRun) return true;
  const name = String(itemField(ability, 'item_name') ?? 'Teleport');
  if (!ctx.teleportOffers) {
    // A frame whose saver has no earned-choice outbox keeps its effect/damage and names only the due grant.
    const creature = String(ecLean(caster, 'encounter_creature_name') || itemField(caster, 'item_name') || 'Creature');
    ctx.rows.push(skipUnported(`teleport-choice-pending:${escapeHtml(creature)}:${escapeHtml(name)}:${rangeFt}ft`));
    return true;
  }
  ctx.teleportOffers.push({ id: ctx.read.uuid(), casterId, abilityId, name, rangeFt,
    origin: { x: posCoord(caster, 'x_pos') ?? 0, y: posCoord(caster, 'y_pos') ?? 0, z: posCoord(caster, 'z_pos') ?? 0 },
    context: ['in_combat', 'active_combat_id', 'current_round', 'current_encounter_creature_id_turn', 'turn_phase']
      .map(key => String(encounterField(ctx.encounter, key) ?? '')),
    triggered: !!ctx.trigger, expires: Math.floor(Date.now() / 1000) + 300 });
  return true;
}
